import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import _get from 'lodash/get';

import { Currency } from '../../../core/api/general/general.types';
import {
  BasketActionsType,
  BasketItemType,
} from '../../../core/types/basket.types';
import { formatNumberToCurrency } from '../../../utils/basketUtils';
import { PriceTag, QuantityControl, Typography } from '../../atoms';

export interface BasketItemProps {
  actions?: BasketActionsType;
  basketItem: BasketItemType;
  currency: Currency;
  hasQuantityControl?: boolean;
  languageCode: string;
  menuId: number;
  translate: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  basketItem: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0),
    width: '100%',

    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  basketContent: {
    paddingRight: theme.spacing(1),
    wordBreak: 'break-word',
  },
  basketControls: {
    minWidth: '100px',
  },
  itemName: {
    maxHeight: theme.spacing(9),
    overflow: 'hidden',
    paddingTop: theme.spacing(0.75),
  },
  itemOption: {
    marginTop: theme.spacing(1),
  },
}));

export function BasketItem(props: BasketItemProps): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    basketItem,
    currency,
    hasQuantityControl = true,
    languageCode,
    menuId,
    translate,
  } = props;

  const { item, options, quantity } = basketItem;

  const hasOptions = !!options?.length;

  const optionsPrice = options.reduce((sum, option) => {
    // eslint-disable-next-line no-param-reassign
    sum += _get(option, 'Price');
    return sum;
  }, 0);

  const total = (item.Price + optionsPrice) * quantity;

  const localisedTotal = formatNumberToCurrency(total, currency, languageCode);

  const onHandleAddItem = (): void => {
    const addItemData = {
      Item: basketItem.item,
      Options: basketItem.options,
    };

    actions.onAddItem(addItemData);
  };

  const onHandleRemoveItem = (): void => {
    const removeItemData = {
      index: basketItem.index,
      indexName: basketItem.item.Name,
      menuId,
    };

    actions.onRemoveItem(removeItemData);
  };

  const basketItemStyles = clsx('fd-basketitem', classes.basketItem);

  const basktOptionsString = options.map((option) => option.Name).join(', ');

  const isQuantityVisible = quantity > 1 && !hasQuantityControl;

  return (
    <div
      aria-label={`${quantity} ${item.Name} @ ${localisedTotal}`}
      className={basketItemStyles}
    >
      <div className={classes.basketContent}>
        <Typography variant="h3" className={classes.itemName}>
          {isQuantityVisible ? `${quantity}x ${item.Name}` : `${item.Name}`}
        </Typography>

        {hasOptions && (
          <Typography className={classes.itemOption} variant="body2-secondary">
            {basktOptionsString}
          </Typography>
        )}
      </div>

      <div className={classes.basketControls}>
        <PriceTag
          hasMenuOptions={false}
          horizontalAlign="right"
          localisedPrice={localisedTotal}
          translate={translate}
        />
        {hasQuantityControl && (
          <QuantityControl
            addItemButtonColor="secondary"
            itemName={item.Name}
            onAddItem={onHandleAddItem}
            onRemoveItem={onHandleRemoveItem}
            quantity={quantity}
            translate={translate}
          />
        )}
      </div>
    </div>
  );
}
